import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  Layout,
  Checkbox,
  Typography,
  Tooltip,
  Row,
  Col,
} from 'antd';
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  QuestionCircleOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { getDistinctValues } from 'utils/normalizers';
import { ActionsNames, isAdmin, isStaff } from 'utils/permission';
import Menu from 'components/Layout/Menu';
import routesToMenuItems from 'components/Layout/routesToMenuItems';
import Select from 'components/Select';
import PlainTransfer from 'components/PlainTransfer';

const { Title, Text } = Typography;
const routes = JSON.parse(localStorage.getItem('allRoutes'));

// eslint-disable-next-line no-unused-vars
const requiredPermissions = [
  'view_usuario', // usuarios/id, usuarios/login
  // general
  'view_estadosglobales',
  // general ??
  'direccion',
];

// eslint-disable-next-line no-unused-vars
const permisosInexistentes = [
  // faltan varios viewcrud/autorizar
  // import/export/cancel en todos los casos necesarios
  'viewcrud_group',
  'view_caja_en_menu',
  'view_ventanilla_en_menu',
  'cobrar_publico_general',
  'view_constanciadesituacionmunicipal',
  'aplicacion_pagosexternos',
  'consulta_pagosexternos',
  'view_reportesrecaudacion',
  'cancelar_recibo',
  'add_observacionesrecibo',
  'desligar_factura',
  'cancelar_pago',
  'view_tramite_vehiculares',
  'reasignarterminal_corte ',
  'reportes_tramite ',
  'reportes_contabilidad ',
];

const Permisos = ({ formItemName }) => {
  const permisos = useSelector(({ catalogs }) => catalogs.permisos);
  const form = Form.useFormInstance();
  const permisosSeleccionadosIds = Form.useWatch(formItemName, form) || [];
  const permisosSeleccionados = permisosSeleccionadosIds
    .map((pId) => permisos.find((p) => p.id === pId)?.codename);
  const [initial, setInitial] = useState([]);
  const [selected, setSelectedItem] = useState();
  const [visiblePreview, setVisiblePreview] = useState(false);

  useEffect(() => {
    const _initial = form.getFieldValue(formItemName);
    setInitial(_initial.sort());
  }, []);

  const getClassName = (route, children) => {
    if (children) {
      if (children.every((e) => e.className === 'disabled')) return 'disabled';
      return null;
    }
    const { permissionModel, mainPermission, required = [] } = route.permissionConfig;
    if (permissionModel && !permisosSeleccionados
      .includes(`viewcrud_${permissionModel}`)) return 'disabled';
    if (mainPermission && ![mainPermission, ...required]
      .every((p) => permisosSeleccionados.includes(p))) return 'disabled';
    return null;
  };

  const hide = (route, children) => {
    if (!visiblePreview) return false;
    if (children) return children.every((e) => e.hidden);
    const { mainPermission, permissionModel } = route.permissionConfig;
    if (!mainPermission && !permissionModel) return false;
    if (mainPermission) return !permisosSeleccionados.includes(mainPermission);
    if (permissionModel) return !permisosSeleccionados.includes(`viewcrud_${permissionModel}`);
    return true;
  };

  const items = routesToMenuItems({
    routes,
    forPermission: true,
    getClassName,
    hide,
  });

  const codeNamesToIDs = (list) => getDistinctValues(list)
    .map((codename) => permisos.find((p) => p.codename === codename)?.id).filter((e) => e);

  const setPermisos = (p) => form.setFieldsValue({ [formItemName]: codeNamesToIDs(p) });

  const onChangeOption = (value, checked, customPermissions = []) => {
    const [action, model] = value.split('_');
    if (!checked) {
      let copy = [...permisosSeleccionados].filter((p) => p !== value);
      if (action === 'viewcrud') {
        // definir como manejar la situacion de se remueve el permisos del crud
        // - remover los de catalogos que no tuviera antes? (lo guardado)
        // - mantenerlos y dejar a criterio del usuario
        // const toRemove = selected.catalogsModels
        //   .map((codename) => permisos.find((p) => p.codename === codename)?.id)
        //   .filter((cp) => !initial.includes(cp));
        // copy = copy.filter((p) => !toRemove.includes(p));
      }
      if (action === 'custom') {
        copy = copy.filter((p) => !customPermissions.includes(p));
      }
      setPermisos(copy);
      return;
    }
    const permissionsRequired = [...permisosSeleccionados, value];
    if (action === 'viewcrud') {
      permissionsRequired.push(`view_${model}`);
      permissionsRequired.push(...selected.optionsCatalogs.map((e) => e.value));
    }
    if (action === 'custom') {
      permissionsRequired.pop();
    }
    permissionsRequired.push(...customPermissions);
    setPermisos(permissionsRequired);
  };

  const optionsMap = (e) => {
    const exist = permisos.find((p) => p.codename === e.value);
    const style = {};
    if (exist) {
      // removed
      if (initial.includes(exist.id) && !permisosSeleccionadosIds.includes(exist.id)) {
        style.color = 'red';
      }
      // added
      if (!initial.includes(exist.id) && permisosSeleccionadosIds.includes(exist.id)) {
        style.color = 'green';
      }
    } else {
      style.color = 'gold';
    }
    if (!e.value) {
      style.display = 'block';
      style.visibility = 'hidden';
      style.height = 0;
    }
    return {
      value: e.value,
      label: (
        <>
          {e.label}
          {' '}
          {!exist && (
            <Tooltip title={`No se encontró permiso con codename '${e.value}', verifique que el permiso exista y este escrito correctamente`}>
              <QuestionCircleOutlined />
            </Tooltip>
          )}
        </>
      ),
      disabled: e.disabled,
      onChange: e.onChange,
      style,
    };
  };

  const getOptions = (model, main = false) => [
    main && {
      value: `viewcrud_${model}`,
      label: 'Ver CRUD',
    },
    {
      value: `view_${model}`,
      label: main ? 'Consultar en otros módulos' : 'Ver',
    },
    {
      value: `add_${model}`,
      label: 'Agregar',
    },
    {
      value: `change_${model}`,
      label: 'Editar',
    },
    {
      value: `delete_${model}`,
      label: 'Eliminar',
    },
    {
      value: `autorizar_${model}`,
      label: 'Autorizar',
    },
  ].filter((e) => e);

  const getOptionsCatalogs = (catalogs = [], disabled = false) => catalogs.map((codename) => ({
    value: codename,
    label: permisos.find((p) => p.codename === codename)?.name.replace('Puede ', '') || codename,
    disabled,
  }));

  const onSelect = (p) => {
    if (visiblePreview) return;
    const { title, permissionconfig: config } = p.item.props;

    config.catalogsModels = config.catalogsModels || [];
    config.required = config.required || [];
    config.customActions = config.customActions || [];
    config.customSections = config.customSections || [];
    config.tabsModels = config.tabsModels || [];

    const mapWithAction = (action = ActionsNames.view) => (m) => (action ? `${action}_${m}` : m);
    const options = [];
    const optionsCatalogs = getOptionsCatalogs((config.catalogsModels).map(mapWithAction()));
    if (config.permissionModel) {
      options.push(...getOptions(config.permissionModel, true));
    }
    if (config.mainPermission) {
      options.push(...getOptionsCatalogs([config.mainPermission, ...config.required], true));
    }
    const customActions = config.customActions.map((action) => ({
      title: action.title,
      options: getOptionsCatalogs(action.permissions, true),
    }));
    const customSections = config.customSections.map((section) => ({
      title: section.title,
      options: getOptionsCatalogs([
        ...(section.models || []).map(mapWithAction(section.action)),
        ...(section.permissions || []),
      ]),
    }));
    const tabs = config.tabsModels.map((tab) => ({
      title: tab.title,
      model: tab.model,
      options: getOptions(tab.model),
      optionsCatalogs: getOptionsCatalogs(tab.catalogs?.map(mapWithAction())),
    }));
    setSelectedItem({
      title,
      model: config.permissionModel,
      mainPermission: config.mainPermission,
      required: config.required,
      selectedKeys: p.selectedKeys,
      options,
      optionsCatalogs,
      customSections,
      customActions,
      tabs,
    });
  };

  const withOnChange = (list) => list.map((item) => ({
    ...item,
    // disabled: item.disabled
    //   || (selected.required.includes(item.value) && permisosSeleccionados.includes(item.value)),
    onChange: (e) => onChangeOption(
      e.target.value,
      e.target.checked,
      /viewcrud|_en_menu/.test(item.value) ? list.map((o) => o.value) : [],
    ),
  })).map(optionsMap);
  const getContent = () => {
    if (!selected || visiblePreview || !selected.options.length) return null;
    const optionsValues = selected.options.map((o) => o.value);
    const disabled = selected.model
      ? !permisosSeleccionados.includes(`viewcrud_${selected.model}`)
      : optionsValues.some((p) => !permisosSeleccionados.includes(p));
    const optionsCatalogsValues = selected.optionsCatalogs.map((o) => o.value);
    const allTabsValues = [
      ...selected.tabs.map((t) => t.options).flat(),
      ...selected.tabs.map((t) => t.optionsCatalogs).flat(),
    ].map((o) => o.value);
    const allActionsValues = selected.customActions
      .map((t) => t.options).flat().map((o) => o.value);
    const everyChecked = (l) => l.every((e) => permisosSeleccionados.includes(e));
    const someChecked = (l) => l.some((e) => permisosSeleccionados.includes(e));
    const someUnchecked = (l) => l.some((e) => !permisosSeleccionados.includes(e));
    const indeterminate = (l) => someChecked(l) && someUnchecked(l);

    return (
      <>
        <Title level={3} style={{ margin: 0 }} strong>
          {selected.model && 'CRUD'}
          {' '}
          {selected.title}
          <CheckAll
            padding="0 0 5px 5px"
            indeterminate={indeterminate(optionsValues)}
            checked={everyChecked(optionsValues)}
            onChange={(e) => onChangeOption(
              'custom',
              e.target.checked,
              [...optionsValues, ...optionsCatalogsValues],
            )}
          />
        </Title>
        <Checkbox.Group
          value={permisosSeleccionados}
          options={withOnChange(selected.options)}
          disabled={disabled}
        />
        {!!selected.optionsCatalogs.length && (
          <div style={{ paddingLeft: 10 }}>
            <Text strong>
              Catálogos
              <CheckAll
                padding="0 0 0px 5px"
                indeterminate={indeterminate(optionsCatalogsValues)}
                checked={everyChecked(optionsCatalogsValues)}
                onChange={(e) => onChangeOption('custom', e.target.checked, optionsCatalogsValues)}
                disabled={disabled}
              />
            </Text>
            <Checkbox.Group
              value={permisosSeleccionados}
              options={withOnChange(selected.optionsCatalogs)}
              disabled={disabled}
            />
          </div>
        )}
        {!!selected.customActions.length && (
          <Title level={3} style={{ margin: '25px 0 0 0' }} strong>
            Acciones
            <CheckAll
              padding="0 0 3px 5px"
              indeterminate={indeterminate(allActionsValues)}
              checked={everyChecked(allActionsValues)}
              onChange={(e) => onChangeOption('custom', e.target.checked, allActionsValues)}
              disabled={disabled}
            />
          </Title>
        )}
        {selected.customActions.map((action, idx) => {
          const actionOptionsValues = action.options.map((o) => o.value);
          return (
            <div key={action.title} style={{ margin: `${idx ? '10px' : 0} 0 0 15px` }}>
              <Title level={5} strong>
                {action.title}
                <CheckAll
                  padding="0 0 3px 5px"
                  indeterminate={indeterminate(actionOptionsValues)}
                  checked={everyChecked(actionOptionsValues)}
                  onChange={(e) => onChangeOption('custom', e.target.checked, actionOptionsValues)}
                  disabled={disabled}
                />
              </Title>
              <Checkbox.Group
                value={permisosSeleccionados}
                options={withOnChange(action.options)}
                disabled={disabled}
              />
            </div>
          );
        })}
        {!!selected.tabs.length && (
          <Title level={3} style={{ margin: '25px 0 0 0' }} strong>
            Tabs
            <CheckAll
              padding="0 0 3px 5px"
              indeterminate={indeterminate(allTabsValues)}
              checked={everyChecked(allTabsValues)}
              onChange={(e) => onChangeOption('custom', e.target.checked, allTabsValues)}
            />
          </Title>
        )}
        {selected.tabs.map((tab, idx) => {
          const tabOptionsValues = tab.options.map((o) => o.value);
          const tabOptionsCatalogsValues = tab.optionsCatalogs.map((o) => o.value);
          const allTabOptionValues = [...tabOptionsValues, ...tabOptionsCatalogsValues];
          return (
            <div key={tab.model} style={{ margin: `${idx ? '10px' : 0} 0 0 15px` }}>
              <Title level={4} strong>
                {tab.title || tab.model}
                <CheckAll
                  padding="0 0 3px 5px"
                  indeterminate={indeterminate(tabOptionsValues)}
                  checked={everyChecked(allTabOptionValues)}
                  onChange={(e) => onChangeOption('custom', e.target.checked, allTabOptionValues)}
                />
              </Title>
              <Checkbox.Group
                value={permisosSeleccionados}
                options={withOnChange(tab.options)}
                disabled={disabled}
              />
              {!!tab.optionsCatalogs.length && (
                <div style={{ paddingLeft: 10 }}>
                  <Text strong>
                    Catálogos:
                    <CheckAll
                      padding="0 0 0px 5px"
                      indeterminate={indeterminate(tabOptionsCatalogsValues)}
                      checked={everyChecked(tabOptionsCatalogsValues)}
                      onChange={(e) => onChangeOption('custom', e.target.checked, tabOptionsCatalogsValues)}
                      disabled={disabled}
                    />
                  </Text>
                  <Checkbox.Group
                    value={permisosSeleccionados}
                    options={withOnChange(tab.optionsCatalogs)}
                    disabled={disabled}
                  />
                </div>
              )}
            </div>
          );
        })}
        {selected.customSections.map((section) => {
          const tabOptionsValues = section.options.map((o) => o.value);
          return (
            <div key={section.title}>
              <Title level={4} style={{ margin: '10px 0 0 0' }} strong>
                {section.title}
                <CheckAll
                  padding="0 0 3px 5px"
                  indeterminate={indeterminate(tabOptionsValues)}
                  checked={everyChecked(tabOptionsValues)}
                  onChange={(e) => onChangeOption('custom', e.target.checked, tabOptionsValues)}
                  disabled={disabled}
                />
              </Title>
              <Checkbox.Group
                value={permisosSeleccionados}
                options={withOnChange(section.options)}
                disabled={disabled}
              />
            </div>
          );
        })}
      </>
    );
  };

  const disabledReset = initial.join() === [...permisosSeleccionadosIds].sort().join();

  return (
    <Col span={24}>
      <StyledLayout style={{ marginBottom: 20 }}>
        <StyledLayout.Sider
          collapsed={false}
          trigger={null}
          theme="dark"
          width={300}
        >
          <Button
            type="link"
            className="no-color"
            onClick={() => setVisiblePreview(!visiblePreview)}
          >
            {visiblePreview ? 'Mostrar todo' : 'Mostrar vista previa'}
            {visiblePreview ? <EyeOutlined /> : <EyeInvisibleOutlined />}
          </Button>
          {visiblePreview && (
            <Button
              style={{ float: 'right' }}
              type="link"
            >
              <Tooltip
                title="Específicamente para configuración actual, si el usuario tiene otros permisos/roles el resultado se puede ver afectado"
              >
                <QuestionCircleOutlined />
              </Tooltip>
            </Button>
          )}
          <Menu
            items={items}
            onSelect={onSelect}
            selectedKeys={visiblePreview ? [] : selected?.selectedKeys}
          />
        </StyledLayout.Sider>
        <StyledLayout style={{ background: 'transparent', padding: 0 }}>
          <StyledLayout.Content>
            <Row justify="end">
              <Button
                disabled={disabledReset}
                onClick={() => form.resetFields([formItemName])}
              >
                Restaurar
                <RollbackOutlined />
              </Button>
            </Row>
            {getContent()}
          </StyledLayout.Content>
        </StyledLayout>
      </StyledLayout>
      {(isAdmin() || isStaff()) ? (
        <PlainTransfer
          dataSource={permisos.sort((a, b) => a.id - b.id)}
          label="Permisos"
          formItemName="permissions"
          filterProp="name"
          showSelectAll
        />
      ) : (
        <Form.Item name={formItemName} hidden>
          <Select />
        </Form.Item>
      )}
    </Col>
  );
};

Permisos.propTypes = {
  formItemName: PropTypes.string.isRequired,
};

const StyledLayout = styled(Layout)`
  background: ${({ theme }) => theme.contentBackgroundColor};
  &.ant-layout-has-sider {
    height: auto;
  }
  .ant-layout-content {
    position: unset;
    height: auto;
  }
  .ant-layout-sider {
    .side-menu {
      height: 500px;
      /* min-height: 40vh; */
    }
    .ant-menu {
      /* overflow: unset !important; */
      .ant-menu-item-icon {
        display: none;
      }
      /* .ant-menu-item-selected {
        background: repeating-linear-gradient(45deg, ${({ theme }) => theme.activeBackgroundColor}, ${({ theme }) => theme.activeBackgroundColor} 6px, transparent 6px, transparent 30px) !important;
        &.disabled {
          background-color: #f5f5f5;
          background: repeating-linear-gradient(45deg, ${({ theme }) => theme.activeBackgroundColor}, ${({ theme }) => theme.activeBackgroundColor} 6px, #f5f5f5 6px, #f5f5f5 30px) !important;
        }
      } */
    }
  }
  .ant-layout-sider-children .ant-btn.ant-btn-link * {
    color: #FFFFFF !important;
  }
  .ant-menu-item.disabled, .ant-menu-submenu.disabled {
    /* text-decoration: line-through !important; */
    .ant-menu-submenu-arrow:before, .ant-menu-submenu-arrow:after {
        background-image: linear-gradient(to right,#000000,#000000) !important;
      }
    .ant-menu-title-content {
      color: #000000 !important;
    }
  }
  .ant-checkbox-group {
    padding-left: 10px;
  }
  span.ant-typography {
    display: block;
    margin: 15px 0 0 0;
  }
  .ant-checkbox-disabled+span {
    color: #000000D9;
  }
  .ant-checkbox-wrapper[style="color: red;"] span:not(.ant-checkbox) {
    color: red;
  }
  .ant-checkbox-wrapper[style="color: green;"] span:not(.ant-checkbox) {
    color: green;
  }
  .ant-checkbox-wrapper[style="color: gold;"] span:not(.ant-checkbox) {
    color: gold;
  }
  .ant-checkbox-wrapper[style] span:not(.ant-checkbox) {
    font-weight: bold;
  }
`;

const CheckAll = styled(Checkbox)`
  .ant-checkbox {
    padding: ${({ padding }) => padding};
  }
`;

export default Permisos;
